import Breadcrumb from '../components/Breadcrumb'

const PrivacyPolicy = () => {
  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Privacy Policy' }
  ]

  return (
    <div className="min-h-screen bg-white">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-dark-background to-dark-plum text-white py-12">
        <div className="container-custom">
          <Breadcrumb
            items={breadcrumbItems}
            className="mb-6 text-gray-300"
          />
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              Privacy Policy
            </h1>
            <p className="text-xl text-gray-200">
              How we collect, use, and protect your personal information
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <div className="space-y-8">

              <div>
                <h2 className="text-2xl font-semibold text-dark-plum mb-4">Introduction</h2>
                <p className="text-gray-600 mb-4">
                  Gaia Powered Ltd is committed to protecting your privacy and ensuring your personal data is
                  handled in accordance with applicable data protection laws, including the UK General Data
                  Protection Regulation (UK GDPR).
                </p>
                <p className="text-gray-600 mb-4">
                  This Privacy Policy explains how we collect, use, store, and protect your personal information
                  when you use our website, request quotes, or engage with our services.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-dark-plum mb-4">Who we are</h2>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-600 mb-2">
                    <strong>Data Controller:</strong> Gaia Powered Ltd
                  </p>
                  <p className="text-gray-600 mb-2">
                    <strong>Address:</strong> {import.meta.env.VITE_COMPANY_ADDRESS}
                  </p>
                  <p className="text-gray-600 mb-2">
                    <strong>Email:</strong> {import.meta.env.VITE_COMPANY_PRIVACY_EMAIL}
                  </p>
                  <p className="text-gray-600">
                    <strong>Phone:</strong> {import.meta.env.VITE_COMPANY_PHONE}
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-dark-plum mb-4">Information we collect</h2>

                <div className="space-y-6">
                  <div className="border-l-4 border-primary-green pl-6">
                    <h3 className="text-xl font-semibold text-dark-plum mb-2">Quote Request Information</h3>
                    <p className="text-gray-600 mb-2">When you request a quote, we collect:</p>
                    <ul className="list-disc list-inside text-gray-600 space-y-1 mb-4">
                      <li>Personal details (name, email address, phone number)</li>
                      <li>Property information (postcode, property type, address)</li>
                      <li>Service-specific information (roof type, energy bills, vehicle details, parking situation)</li>
                      <li>Additional requirements or questions you provide</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-primary-green pl-6">
                    <h3 className="text-xl font-semibold text-dark-plum mb-2">Website Usage Information</h3>
                    <p className="text-gray-600 mb-2">When you visit our website, we may automatically collect:</p>
                    <ul className="list-disc list-inside text-gray-600 space-y-1 mb-4">
                      <li>IP address and browser information</li>
                      <li>Pages visited and time spent on our website</li>
                      <li>Referral source and exit pages</li>
                      <li>Device and operating system information</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-primary-green pl-6">
                    <h3 className="text-xl font-semibold text-dark-plum mb-2">Communication Records</h3>
                    <p className="text-gray-600 mb-2">We may keep records of:</p>
                    <ul className="list-disc list-inside text-gray-600 space-y-1 mb-4">
                      <li>Email communications</li>
                      <li>Phone call records and notes</li>
                      <li>Support enquiries and responses</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-dark-plum mb-4">How we use your information</h2>

                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                    <h4 className="font-semibold text-dark-plum mb-2">Primary Purposes:</h4>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Provide personalised quotes for renewable energy installations</li>
                      <li>Contact you about your quote requests and follow-up enquiries</li>
                      <li>Arrange site visits and consultations</li>
                      <li>Process and fulfill service contracts</li>
                    </ul>
                  </div>

                  <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                    <h4 className="font-semibold text-dark-plum mb-2">Secondary Purposes:</h4>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Improve our website and services</li>
                      <li>Send relevant information about our services (with your consent)</li>
                      <li>Comply with legal and regulatory requirements</li>
                      <li>Prevent fraud and ensure website security</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-dark-plum mb-4">Legal basis for processing</h2>
                <p className="text-gray-600 mb-4">We process your personal data based on:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-dark-plum mb-2">Legitimate Interest</h4>
                    <p className="text-sm text-gray-600">
                      Providing quotes and following up on enquiries
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-dark-plum mb-2">Consent</h4>
                    <p className="text-sm text-gray-600">
                      Marketing communications and non-essential cookies
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-dark-plum mb-2">Contract</h4>
                    <p className="text-sm text-gray-600">
                      Fulfilling installation and service contracts
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-dark-plum mb-2">Legal Obligation</h4>
                    <p className="text-sm text-gray-600">
                      Compliance with industry regulations
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-dark-plum mb-4">Information sharing</h2>
                <p className="text-gray-600 mb-4">
                  We do not sell your personal information. We may share your information with:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
                  <li><strong>Service providers:</strong> Third-party quote systems</li>
                  <li><strong>Installation partners:</strong> Certified technicians who will perform your installation</li>
                  <li><strong>Legal compliance:</strong> Authorities when required by law</li>
                  <li><strong>Business protection:</strong> To protect our rights and prevent fraud</li>
                </ul>
                <p className="text-gray-600">
                  All third parties are required to protect your information and use it only for the specified purposes.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-dark-plum mb-4">Data retention</h2>
                <p className="text-gray-600 mb-4">We retain your personal information for:</p>
                <div className="space-y-3">
                  <div className="flex justify-between items-center border-b pb-2">
                    <span className="font-medium text-dark-plum">Quote requests (not proceeded)</span>
                    <span className="text-gray-600">2 years</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-2">
                    <span className="font-medium text-dark-plum">Customer contracts and records</span>
                    <span className="text-gray-600">7 years (legal requirement)</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-2">
                    <span className="font-medium text-dark-plum">Marketing communications</span>
                    <span className="text-gray-600">Until you unsubscribe</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-dark-plum">Website analytics</span>
                    <span className="text-gray-600">26 months (Google Analytics)</span>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-dark-plum mb-4">Your rights</h2>
                <p className="text-gray-600 mb-4">Under UK GDPR, you have the right to:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-dark-plum mb-2">Access</h4>
                    <p className="text-sm text-gray-600">Request a copy of your personal data</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-dark-plum mb-2">Rectification</h4>
                    <p className="text-sm text-gray-600">Correct inaccurate personal data</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-dark-plum mb-2">Erasure</h4>
                    <p className="text-sm text-gray-600">Request deletion of your personal data</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-dark-plum mb-2">Portability</h4>
                    <p className="text-sm text-gray-600">Receive your data in a structured format</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-dark-plum mb-2">Restriction</h4>
                    <p className="text-sm text-gray-600">Limit how we process your data</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-dark-plum mb-2">Objection</h4>
                    <p className="text-sm text-gray-600">Object to processing for marketing purposes</p>
                  </div>
                </div>
                <p className="text-gray-600 mt-4">
                  To exercise any of these rights, please contact us using the details provided above.
                  We'll respond within one month of receiving your request.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-dark-plum mb-4">Data security</h2>
                <p className="text-gray-600 mb-4">
                  We implement appropriate technical and organisational measures to protect your personal data:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
                  <li>SSL encryption for data transmission</li>
                  <li>Secure data storage and access controls</li>
                  <li>Regular security assessments and updates</li>
                  <li>Staff training on data protection</li>
                  <li>Incident response procedures</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-dark-plum mb-4">International transfers</h2>
                <p className="text-gray-600 mb-4">
                  Some of our service providers may process your data outside the UK. When this occurs,
                  we ensure appropriate safeguards are in place, such as:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>Adequacy decisions by the UK authorities</li>
                  <li>Standard contractual clauses</li>
                  <li>Binding corporate rules</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-dark-plum mb-4">Changes to this policy</h2>
                <p className="text-gray-600 mb-4">
                  We may update this Privacy Policy from time to time to reflect changes in our practices
                  or applicable laws. We'll notify you of any material changes by posting the updated
                  policy on our website and updating the "last updated" date below.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-dark-plum mb-4">Complaints</h2>
                <p className="text-gray-600 mb-4">
                  If you have concerns about how we handle your personal data, you can:
                </p>
                <ol className="list-decimal list-inside text-gray-600 space-y-2 mb-4">
                  <li>Contact us directly using the details above</li>
                  <li>Lodge a complaint with the Information Commissioner's Office (ICO) at ico.org.uk</li>
                </ol>
              </div>

              <div className="border-t pt-6">
                <p className="text-sm text-gray-500">
                  <strong>Last updated:</strong> 23 April 2026
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default PrivacyPolicy